import type { DateTime } from 'luxon';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { convertSelectionToDateTime } from '../Timeline.utils';
// import { useSelectionContainer } from '@air/react-drag-to-select';
import { useSelectionContainer } from './drag-select/useSelectionContainer';
import { TimelineContext } from './Timeline.Context';
import styles from './Timeline.Selection.module.scss';

type Box = { top: number; left: number; height: number; width: number };

const EMPTY_POSITION: Box = { top: 0, height: 0, left: 0, width: 0 };

type SelectionProps = {
  /** Returns start/end time of selection (assuming `Timeline.fromDateTime` is set ) */
  onSelectionEnd?: (params: {
    action: 'add' | 'remove';
    started_at: DateTime;
    ended_at: DateTime;
  }) => void;
  snapToMinutes?: number;
};

/**
 * Allow click-to-select across the Timeline
 * `@air/react-drag-to-select` is doing all the heavy lifting here
 * @note Consider the existing hooks, but discuss with @0rvar about preventDefault stuff.
 */
export const TimelineSelection = (props: SelectionProps) => {
  const { snapToMinutes = 1 } = props;
  // Position & size of the selection box. This state only drives the rendered
  // overlay: it is written from `onSelectionChange`, which the drag-select hook
  // defers to the next animation frame, so it lags the gesture by up to a
  // frame. The range we report comes from the box handed to `onSelectionEnd`
  // instead — a fast drag can finish before that frame ever runs.
  const [position, setPosition] = useState(EMPTY_POSITION);
  // Whether a gesture is in flight. Frames queued during a drag can run after
  // it ended; this stops them repainting the overlay with dead geometry.
  const isSelectingRef = useRef(false);
  const {
    fromDateTime,
    scrollAreaRef,
    orientation,
    pxPerMinute,
    isDragMode,
    isAltKeyHeld,
    setIsDragMode,
  } = useContext(TimelineContext);

  // Viewport-relative selection box → position within the scrollable content.
  // Reads the container's viewport offset live rather than caching it at drag
  // start: the page can scroll under a gesture (anything that scrolls the
  // timeline into view when the drag begins), and a cached offset would then
  // place the range wherever the container used to be.
  const toContentPosition = (selection: Box): Box => {
    const { scrollTop = 0, scrollLeft = 0 } = scrollAreaRef || {};
    const { top = 0, left = 0 } = scrollAreaRef?.getBoundingClientRect() || {};
    return {
      top: selection.top + scrollTop - top,
      left: selection.left + scrollLeft - left,
      height: selection.height,
      width: selection.width,
    };
  };

  const { DragSelection } = useSelectionContainer({
    eventsElement: scrollAreaRef,
    shouldStartSelecting: eventTarget => {
      // Prevent dragging on inputs and other opted-out surfaces. `Element`, not
      // `HTMLElement`: a press often lands on an SVG icon inside the opted-out
      // block, and SVG nodes are not HTMLElements.
      if (eventTarget instanceof Element && eventTarget.closest('[data-no-drag-select]')) {
        return false;
      }
      // Resizable timestamps identify as `data-timestamp-adjuster` to block selection
      const isAdjustHandle = (eventTarget as HTMLElement)?.closest('[data-timestamp-adjuster]');
      return !isDragMode && !isAdjustHandle;
    },
    onSelectionChange: selection => {
      if (!isSelectingRef.current) return;
      setPosition(toContentPosition(selection));
    },
    onSelectionStart: () => {
      isSelectingRef.current = true;
      setIsDragMode(true);
    },
    onSelectionEnd: (_event, selection) => {
      isSelectingRef.current = false;
      setIsDragMode(false);
      setPosition(EMPTY_POSITION);
      if (!selection) return;

      const finalPosition = toContentPosition(selection);
      if (finalPosition.width > pxPerMinute || finalPosition.height > pxPerMinute) {
        const { started_at, ended_at } = convertSelectionToDateTime({
          position: finalPosition,
          orientation,
          pxPerMinute,
          fromDateTime,
          snapToMinutes,
        });
        // A registered drag whose endpoints snap to the same tick still means
        // "select here": round it up to the smallest legal range instead of
        // silently dropping the gesture.
        const minimalEndedAt =
          started_at.toMillis() === ended_at.toMillis()
            ? started_at.plus({ minutes: snapToMinutes })
            : ended_at;
        props.onSelectionEnd?.({
          started_at,
          ended_at: minimalEndedAt,
          action: isAltKeyHeld ? 'remove' : 'add',
        });
      }
    },
    // Don't actually render the selection box, we'll draw our own UI
    selectionProps: { style: { display: 'none' } },
  });

  const style = useMemo((): React.CSSProperties => {
    if (!isDragMode) return {};
    function snap(px: number) {
      return Math.round(px / pxPerMinute / snapToMinutes) * snapToMinutes * pxPerMinute;
    }
    if (orientation === 'vertical') {
      const top = position.top;
      const topSnapped = snap(top);
      const heightAdjustment = topSnapped - top;
      const height = snap(position.height - heightAdjustment);
      return { top: topSnapped, height, left: 0, right: 0 };
    }
    const left = position.left;
    const leftSnapped = snap(left);
    const widthAdjustment = leftSnapped - left;
    const width = snap(position.width - widthAdjustment);
    return { top: 0, bottom: 0, left: leftSnapped, width };
  }, [isDragMode, orientation, position, pxPerMinute, snapToMinutes]);

  return (
    <>
      <DragSelection />
      {isDragMode && <div className={styles.selectedArea} style={style} />}
    </>
  );
};
TimelineSelection.displayName = 'Timeline.Selection';
